import { useState, useEffect, useMemo, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  localDb, 
  generateId, 
  LocalTransaction, 
  LocalCategory,
  LocalTransactionTag,
  initializeLocalDatabase
} from '@/lib/localDatabase';

export interface LocalTransactionWithDetails extends LocalTransaction {
  category?: LocalCategory | null;
  transaction_tags?: Array<{
    id: string;
    tag_id: string;
    tag: {
      id: string;
      name: string;
      color: string;
    };
  }>;
}

export function useLocalTransactions() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rawTransactions, setRawTransactions] = useState<LocalTransaction[]>([]);
  const [categories, setCategories] = useState<LocalCategory[]>([]);
  const [tags, setTags] = useState<{ id: string; name: string; color: string }[]>([]);
  const [transactionTags, setTransactionTags] = useState<LocalTransactionTag[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Initialize and fetch data - always run on mount and when refreshKey changes
  useEffect(() => {
    let isMounted = true;
    
    const initAndFetch = async () => {
      try {
        // Always ensure database is initialized
        await initializeLocalDatabase();
        
        if (!isMounted) return;
        
        const [txs, cats, allTags, allTxTags] = await Promise.all([
          localDb.transactions.getAll(),
          localDb.categories.getAll(),
          localDb.tags.getAll(),
          localDb.transactionTags.getAll(),
        ]);
        
        if (!isMounted) return;
        
        setRawTransactions(txs);
        setCategories(cats);
        setTags(allTags);
        setTransactionTags(allTxTags);
        setLoading(false);
      } catch (error) {
        console.error('Error initializing/fetching local data:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initAndFetch();
    
    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  // Combine transactions with their categories and tags
  const transactions = useMemo(() => {
    return rawTransactions.map(transaction => {
      const category = categories.find(c => c.id === transaction.category_id);
      
      const txTags = transactionTags
        .filter(tt => tt.transaction_id === transaction.id)
        .map(tt => {
          const tag = tags.find(t => t.id === tt.tag_id);
          if (!tag) return null;
          return {
            id: tt.id,
            tag_id: tt.tag_id,
            tag: {
              id: tag.id,
              name: tag.name,
              color: tag.color,
            },
          };
        })
        .filter(Boolean) as LocalTransactionWithDetails['transaction_tags'];

      return {
        ...transaction,
        user_id: 'local',
        category: category || null,
        transaction_tags: txTags,
      } as LocalTransactionWithDetails;
    });
  }, [rawTransactions, categories, tags, transactionTags]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // Add transaction
  const addTransaction = useCallback(async (data: {
    type: 'income' | 'expense';
    amount: number;
    description?: string;
    category_id?: string;
    date?: string;
    tag_ids?: string[];
  }) => {
    try {
      const now = new Date().toISOString();
      const id = generateId();

      const transaction: LocalTransaction = {
        id,
        type: data.type,
        amount: data.amount,
        description: data.description || null,
        category_id: data.category_id || null,
        date: data.date || now,
        created_at: now,
        updated_at: now,
        synced: false,
      };

      await localDb.transactions.add(transaction);

      // Add tags if provided
      if (data.tag_ids && data.tag_ids.length > 0) {
        for (const tag_id of data.tag_ids) {
          await localDb.transactionTags.add({
            id: generateId(),
            transaction_id: id,
            tag_id,
            synced: false,
          });
        }
      }

      toast({
        title: 'Успешно',
        description: 'Транзакция добавлена',
      });

      refresh();
      return transaction;
    } catch (error) {
      console.error('Error adding local transaction:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось добавить транзакцию',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, refresh]);

  // Update transaction
  const updateTransaction = useCallback(async (
    id: string, 
    updateData: Partial<LocalTransaction>, 
    tagIds?: string[]
  ) => {
    try {
      await localDb.transactions.update(id, {
        ...updateData,
        updated_at: new Date().toISOString(),
        synced: false,
      });

      // Update tags if provided
      if (tagIds !== undefined) {
        const existingTags = await localDb.transactionTags.getByTransaction(id);
        const existingTagIds = existingTags.map(t => t.tag_id);
        
        // Remove old tags
        for (const tag of existingTags) {
          if (!tagIds.includes(tag.tag_id)) {
            await localDb.transactionTags.delete(tag.id);
          }
        }
        
        // Add new tags
        for (const tagId of tagIds) {
          if (!existingTagIds.includes(tagId)) {
            await localDb.transactionTags.add({
              id: generateId(),
              transaction_id: id,
              tag_id: tagId,
              synced: false,
            });
          }
        }
      }

      toast({
        title: 'Успешно',
        description: 'Транзакция обновлена',
      });

      refresh();
      return true;
    } catch (error) {
      console.error('Error updating local transaction:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить транзакцию',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refresh]);

  // Delete transaction
  const deleteTransaction = useCallback(async (id: string) => {
    try {
      await localDb.transactions.delete(id);
      
      // Also delete related tags
      const relatedTags = await localDb.transactionTags.getByTransaction(id);
      for (const tag of relatedTags) {
        await localDb.transactionTags.delete(tag.id);
      }

      toast({
        title: 'Успешно',
        description: 'Транзакция удалена',
      });

      refresh();
      return true;
    } catch (error) {
      console.error('Error deleting local transaction:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось удалить транзакцию',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refresh]);

  // Memoized calculations
  const { totalIncome, totalExpense, balance, expensesByCategory, incomesByCategory } = useMemo(() => {
    const totalIncome = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const totalExpense = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const balance = totalIncome - totalExpense;

    const expensesByCategory = transactions
      .filter(t => t.type === 'expense')
      .reduce((acc, t) => {
        const categoryId = t.category_id || 'uncategorized';
        if (!acc[categoryId]) {
          acc[categoryId] = {
            category: t.category,
            total: 0,
            transactions: [] as LocalTransactionWithDetails[],
          };
        }
        acc[categoryId].total += Number(t.amount);
        acc[categoryId].transactions.push(t);
        return acc;
      }, {} as Record<string, { category: LocalCategory | null | undefined; total: number; transactions: LocalTransactionWithDetails[] }>);

    const incomesByCategory = transactions
      .filter(t => t.type === 'income')
      .reduce((acc, t) => {
        const categoryId = t.category_id || 'uncategorized';
        if (!acc[categoryId]) {
          acc[categoryId] = {
            category: t.category,
            total: 0,
            transactions: [] as LocalTransactionWithDetails[],
          };
        }
        acc[categoryId].total += Number(t.amount);
        acc[categoryId].transactions.push(t);
        return acc;
      }, {} as Record<string, { category: LocalCategory | null | undefined; total: number; transactions: LocalTransactionWithDetails[] }>);

    return { totalIncome, totalExpense, balance, expensesByCategory, incomesByCategory };
  }, [transactions]);

  return {
    transactions: transactions as any[],
    categories: categories as any[],
    loading,
    user: { id: 'local' },
    addTransaction,
    updateTransaction,
    deleteTransaction,
    fetchTransactions: refresh,
    fetchCategories: refresh,
    totalIncome,
    totalExpense,
    balance,
    expensesByCategory,
    incomesByCategory,
  };
}
